import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
	Badge,
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Input,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@ancatag/at-design";
import {
	AlertTriangle,
	RotateCcw,
	Search,
	UserCheck,
	UserX,
} from "lucide-react";
import type { ChangeEvent } from "react";
import { useState } from "react";
import { toast } from "sonner";
import {
	useActivateUser,
	useDeactivateUser,
	useResetUserCounters,
	useSuspendUser,
	useUsers,
} from "@/hooks/admin";
import type { User } from "@/types/admin";

export function Users() {
	const [page, setPage] = useState(1);
	const [search, setSearch] = useState("");
	const [statusFilter, setStatusFilter] = useState<string>("all");
	const [roleFilter, setRoleFilter] = useState<string>("all");

	const { data: usersData, isLoading } = useUsers(page, 50, {
		search: search || undefined,
		status:
			statusFilter === "all"
				? undefined
				: (statusFilter as "active" | "suspended" | "deleted"),
		role:
			roleFilter === "all"
				? undefined
				: (roleFilter as "user" | "admin" | "superAdmin"),
	});

	const activateUser = useActivateUser();
	const deactivateUser = useDeactivateUser();
	const suspendUser = useSuspendUser();
	const resetCounters = useResetUserCounters();

	const handleActivate = async (userId: string) => {
		try {
			await activateUser.mutateAsync(userId);
			toast.success("User activated successfully");
		} catch {
			toast.error("Failed to activate user");
		}
	};

	const handleDeactivate = async (userId: string) => {
		try {
			await deactivateUser.mutateAsync({ userId });
			toast.success("User deactivated successfully");
		} catch {
			toast.error("Failed to deactivate user");
		}
	};

	const handleSuspend = async (userId: string) => {
		try {
			await suspendUser.mutateAsync({ userId });
			toast.success("User suspended successfully");
		} catch {
			toast.error("Failed to suspend user");
		}
	};

	const handleResetCounters = async (userId: string) => {
		try {
			await resetCounters.mutateAsync(userId);
			toast.success("User counters reset successfully");
		} catch {
			toast.error("Failed to reset counters");
		}
	};

	const getStatusBadge = (status: string) => {
		switch (status) {
			case "active":
				return (
					<Badge variant="default" className="bg-green-500">
						Active
					</Badge>
				);
			case "inactive":
				return <Badge variant="secondary">Inactive</Badge>;
			case "suspended":
				return <Badge variant="destructive">Suspended</Badge>;
			default:
				return <Badge variant="outline">{status}</Badge>;
		}
	};

	const getRoleBadge = (role: string) => {
		switch (role) {
			case "admin":
				return <Badge variant="default">Admin</Badge>;
			case "user":
				return <Badge variant="outline">User</Badge>;
			default:
				return <Badge variant="outline">{role}</Badge>;
		}
	};

	return (
		<div className="space-y-6">
			{/* Header */}
			<div>
				<h1 className="text-3xl font-bold text-foreground">Users</h1>
				<p className="text-muted-foreground mt-2">
					Manage user accounts and permissions
				</p>
			</div>

			{/* Filters */}
			<Card>
				<CardHeader>
					<CardTitle>Filters</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="flex flex-col sm:flex-row gap-4">
						<div className="flex-1">
							<div className="relative">
								<Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
								<Input
									placeholder="Search by email or name..."
									value={search}
									onChange={(e: ChangeEvent<HTMLInputElement>) =>
										setSearch(e.target.value)
									}
									className="pl-10"
								/>
							</div>
						</div>
						<Select value={statusFilter} onValueChange={setStatusFilter}>
							<SelectTrigger className="w-full sm:w-48">
								<SelectValue placeholder="Filter by status" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All Status</SelectItem>
								<SelectItem value="active">Active</SelectItem>
								<SelectItem value="inactive">Inactive</SelectItem>
								<SelectItem value="suspended">Suspended</SelectItem>
							</SelectContent>
						</Select>
						<Select value={roleFilter} onValueChange={setRoleFilter}>
							<SelectTrigger className="w-full sm:w-48">
								<SelectValue placeholder="Filter by role" />
							</SelectTrigger>
							<SelectContent>
								<SelectItem value="all">All Roles</SelectItem>
								<SelectItem value="admin">Admin</SelectItem>
								<SelectItem value="user">User</SelectItem>
							</SelectContent>
						</Select>
					</div>
				</CardContent>
			</Card>

			{/* Users Table */}
			<Card>
				<CardHeader>
					<CardTitle>Users ({usersData?.meta?.total || 0})</CardTitle>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<div className="text-center py-8">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
							<p className="mt-2 text-muted-foreground">Loading users...</p>
						</div>
					) : (
						<div className="overflow-x-auto">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>Email</TableHead>
										<TableHead>Name</TableHead>
										<TableHead>Status</TableHead>
										<TableHead>Role</TableHead>
										<TableHead>Created</TableHead>
										<TableHead>Actions</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{usersData?.data?.map((user: User) => (
										<TableRow key={user.id}>
											<TableCell className="font-medium">
												{user.email}
											</TableCell>
											<TableCell>{user.name || "-"}</TableCell>
											<TableCell>{getStatusBadge(user.status)}</TableCell>
											<TableCell>{getRoleBadge(user.role)}</TableCell>
											<TableCell>
												{new Date(user.createdAt).toLocaleDateString()}
											</TableCell>
											<TableCell>
												<div className="flex gap-2">
													{user.status !== "active" &&
														user.status !== "suspended" && (
															<AlertDialog>
																<AlertDialogTrigger asChild>
																	<Button size="sm" variant="outline">
																		<UserCheck className="w-4 h-4" />
																	</Button>
																</AlertDialogTrigger>
																<AlertDialogContent>
																	<AlertDialogHeader>
																		<AlertDialogTitle>
																			Activate User
																		</AlertDialogTitle>
																		<AlertDialogDescription>
																			Are you sure you want to activate this
																			user account?
																		</AlertDialogDescription>
																	</AlertDialogHeader>
																	<AlertDialogFooter>
																		<AlertDialogCancel>
																			Cancel
																		</AlertDialogCancel>
																		<AlertDialogAction
																			onClick={() => handleActivate(user.id)}
																		>
																			Activate
																		</AlertDialogAction>
																	</AlertDialogFooter>
																</AlertDialogContent>
															</AlertDialog>
														)}

													{user.status === "active" && (
														<AlertDialog>
															<AlertDialogTrigger asChild>
																<Button size="sm" variant="outline">
																	<UserX className="w-4 h-4" />
																</Button>
															</AlertDialogTrigger>
															<AlertDialogContent>
																<AlertDialogHeader>
																	<AlertDialogTitle>
																		Deactivate User
																	</AlertDialogTitle>
																	<AlertDialogDescription>
																		Are you sure you want to deactivate this
																		user account?
																	</AlertDialogDescription>
																</AlertDialogHeader>
																<AlertDialogFooter>
																	<AlertDialogCancel>Cancel</AlertDialogCancel>
																	<AlertDialogAction
																		onClick={() => handleDeactivate(user.id)}
																	>
																		Deactivate
																	</AlertDialogAction>
																</AlertDialogFooter>
															</AlertDialogContent>
														</AlertDialog>
													)}

													{user.status === "active" && (
														<AlertDialog>
															<AlertDialogTrigger asChild>
																<Button size="sm" variant="destructive">
																	<AlertTriangle className="w-4 h-4" />
																</Button>
															</AlertDialogTrigger>
															<AlertDialogContent>
																<AlertDialogHeader>
																	<AlertDialogTitle>
																		Suspend User
																	</AlertDialogTitle>
																	<AlertDialogDescription>
																		Are you sure you want to suspend this user
																		account?
																	</AlertDialogDescription>
																</AlertDialogHeader>
																<AlertDialogFooter>
																	<AlertDialogCancel>Cancel</AlertDialogCancel>
																	<AlertDialogAction
																		onClick={() => handleSuspend(user.id)}
																	>
																		Suspend
																	</AlertDialogAction>
																</AlertDialogFooter>
															</AlertDialogContent>
														</AlertDialog>
													)}

													<Button
														size="sm"
														variant="outline"
														onClick={() => handleResetCounters(user.id)}
													>
														<RotateCcw className="w-4 h-4" />
													</Button>
												</div>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</div>
					)}

					{/* Pagination */}
					{usersData?.meta && usersData.meta.total > 50 && (
						<div className="flex justify-between items-center mt-4">
							<Button
								variant="outline"
								onClick={() => setPage(Math.max(1, page - 1))}
								disabled={page === 1}
							>
								Previous
							</Button>
							<span className="text-sm text-muted-foreground">
								Page {page} of {usersData.meta.totalPages}
							</span>
							<Button
								variant="outline"
								onClick={() => setPage(page + 1)}
								disabled={!usersData.meta.hasNext}
							>
								Next
							</Button>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
